import { createSlice } from "@reduxjs/toolkit";
import { ILabInventory } from "../../pages/lab-inventory/type";

// Define a type for the slice state
interface ILabInventoryState {
  data: Array<ILabInventory> | null;
  pageSize: number;
  pageNumber: number;
  totalCount: number;
  showLoading: ILoading;
  addLabInventoryLoading: boolean;
  addLabInventorySuccess: boolean;
  labInventoryDetails: ILabInventory | null;
  ecaasLeaseAuditDetails: Array<IEcaasLeaseAudit> | null;
  checkoutSummaryDetails: ILabInventory | null,
  autoName: string | null;
  fetchAutoNameLoading: boolean;
  inventoryLeaseOwnerDetails: Array<IUser> | null;
  leaseActionSuccess: boolean;
  leaseResponseDetails: { type: string, data: IEcaasLease | null, message: string, sourcePage: string }
  bulkLeaseResult: {
    result: any | null,
    showResult: boolean
  }
  error: string | null;
  globalSearchIds: number[] | null;
  validSearchCount: number | null;
}

// Define the initial state using that type
const initialState: ILabInventoryState = {
  data: null,
  pageSize: 0,
  pageNumber: 0,
  totalCount: 0,
  showLoading: {
    sourceAction: "",
    state: false,
  },
  addLabInventoryLoading: false,
  addLabInventorySuccess: false,
  labInventoryDetails: null,
  ecaasLeaseAuditDetails: null,
  checkoutSummaryDetails: null,
  autoName: null,
  fetchAutoNameLoading: false,
  inventoryLeaseOwnerDetails: null,
  leaseActionSuccess: false,
  leaseResponseDetails: { type: "", data: null, message: "", sourcePage: "" },
  bulkLeaseResult: {
    result: null,
    showResult: false,
  },
  error: null,
  globalSearchIds: null,
  validSearchCount: null,
};

export const labInventorySlice = createSlice({
  name: "labInventory",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.showLoading = {
        sourceAction: action.payload.sourceAction || "",
        state: action.payload.value ?? true,
      };
      state.addLabInventoryLoading = action.payload.addLabInventoryLoading
        ? action.payload.addLabInventoryLoading
        : false;
      state.addLabInventorySuccess = action.payload.addLabInventorySuccess
        ? action.payload.addLabInventorySuccess
        : false;
    },
    resetLoading: (state, action) => {
      state.showLoading = {
        sourceAction: "",
        state: false,
      };
      state.addLabInventoryLoading = action.payload.addLabInventoryLoading
        ? action.payload.addLabInventoryLoading
        : false;
      state.addLabInventorySuccess = action.payload.addLabInventorySuccess
        ? action.payload.addLabInventorySuccess
        : false;
    },
    replaceLabInventory: (state, action) => {
      state.data = action.payload.data;
      state.pageSize = action.payload.pageSize;
      state.pageNumber = action.payload.pageNumber;
      state.totalCount = action.payload.totalCount;
    },
    addLabInventory: (state, action) => {
      const newItem = action.payload.data;
      if (state.data && state.data.length > state.pageSize) {
        state.data.pop();
      }
      state.data = state.data ? [newItem, ...state.data] : [newItem];
      state.totalCount = state.totalCount + 1;
    },
    updateLabInventory: (state, action) => {
      const { localID, data } = action.payload;
      if (localID && localID >= 0 && state.data) {
        state.data[localID] = data;
      }
      state.labInventoryDetails = data;
    },
    deleteLabInventory: (state, action) => {
      const labInventoryToRemove: ILabInventory = action.payload.data;
      const newLabInventoryData =
        state.data &&
        state.data.filter((labInventory: ILabInventory) => {
          return labInventory.id !== labInventoryToRemove.id;
        });
      state.data = newLabInventoryData;
      state.totalCount = state.totalCount - 1;
    },
    setLabInventoryDetails: (state, action) => {
      state.labInventoryDetails = action.payload.data;
    },
    setCheckoutSummaryDetails: (state, action) => {
      state.checkoutSummaryDetails = action.payload.data;
    },
    setAutoName: (state, action) => {
      const { data } = action.payload;
      state.autoName = data;
    },
    resetAutoNameLoading: (state) => {
      state.fetchAutoNameLoading = !state.fetchAutoNameLoading;
    },
    setInventoryLeaseOwnerDetails: (state, action) => {
      const { data } = action.payload;
      state.inventoryLeaseOwnerDetails = data;
    },
    setLeaseData: (state, action) => {
      state.leaseActionSuccess = action.payload.leaseActionSuccess
        ? action.payload.leaseActionSuccess
        : false;
      state.leaseResponseDetails = action.payload.leaseResponseDetails
        ? action.payload.leaseResponseDetails
        : state.leaseResponseDetails
    },
    setEcaasLeaseAuditDetails: (state, action) => {
      state.ecaasLeaseAuditDetails = action.payload.data;
    },
    setBulkLeaseResult: (state, action) => {
      const { result, showResult } = action.payload;
      state.bulkLeaseResult = { result: result, showResult: showResult };
    },
    setGlobalSearchIds: (state, action) => {
      state.globalSearchIds = action.payload;
    },
    setValidSearchCount: (state, action) => {
      state.validSearchCount = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const labInventoryActions = labInventorySlice.actions;

export default labInventorySlice.reducer;
